// Objectif de l'appareil : position de la caméra dans le monde. Bornée en Y (ciel/sol, jamais de
// pellicule au-delà) ; bouclée en X (voir wrapX) — pas de bord horizontal, un panoramique
// continu vers la droite (ou la gauche) revient d'où on est parti, comme un tour complet.
// Fonctions pures, sans DOM. La caméra désigne le point du monde vu au centre du viseur.
import { VIEW_SIZE } from '../config/constants.js';

export const PAN_DIRECTIONS = Object.freeze({
  up:    { dx: 0,  dy: -1 },
  down:  { dx: 0,  dy: 1 },
  left:  { dx: -1, dy: 0 },
  right: { dx: 1,  dy: 0 },
});

/** Jusqu'où la caméra peut s'éloigner du centre EN Y (ciel/sol) sans que le viseur ne sorte du monde. */
export function getPanLimit(worldSize, viewSize = VIEW_SIZE) {
  return Math.max(0, (worldSize - viewSize) / 2);
}

/**
 * Ramène une abscisse dans [-worldSize/2, worldSize/2) : le monde boucle horizontalement.
 * Sert aussi bien à normaliser une position (caméra, fantôme) qu'à obtenir le plus court écart
 * signé entre deux abscisses, ex. `wrapX(a.x - b.x, worldSize)` (jamais plus de worldSize/2, donc
 * toujours le chemin le plus court, direct ou en passant par la couture). Si `worldSize` est
 * absent ou invalide, renvoie `x` tel quel (pas de bouclage) — pour les appels qui n'en ont pas
 * besoin (ex. seulement l'axe Y) ou les anciens tests qui ignorent le bouclage.
 */
export function wrapX(x, worldSize) {
  if (!(worldSize > 0) || !Number.isFinite(worldSize)) return x;
  const half = worldSize / 2;
  return ((x + half) % worldSize + worldSize) % worldSize - half;
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Déplace la caméra d'une distance dans une direction : boucle en X (wrapX), reste dans les
 * limites du monde en Y.
 * @returns {{x:number,y:number}} nouvelle position (identique si direction inconnue)
 */
export function pan(camera, direction, distance, worldSize) {
  const dir = PAN_DIRECTIONS[direction];
  if (!dir) return { x: camera.x, y: camera.y };
  const limit = getPanLimit(worldSize);
  return {
    x: wrapX(camera.x + dir.dx * distance, worldSize),
    y: clamp(camera.y + dir.dy * distance, -limit, limit),
  };
}

/**
 * Vitesse de l'objectif après `heldSeconds` d'appui continu : démarre à `panSpeed` et accélère
 * linéairement jusqu'à `panMaxSpeed` en `panAccelTime` secondes (aucune accélération si 0).
 */
export function getPanSpeed(heldSeconds, { panSpeed, panMaxSpeed, panAccelTime }) {
  const max = Math.max(panSpeed, panMaxSpeed);
  if (!(panAccelTime > 0)) return max;
  const t = clamp(heldSeconds / panAccelTime, 0, 1);
  return panSpeed + (max - panSpeed) * t;
}

/**
 * Un pas de déplacement continu : les directions maintenues (une ou deux, ex. haut + droite)
 * déplacent la caméra de vitesse × dt, la vitesse dépendant de la durée d'appui.
 * @param {{x:number,y:number}} camera
 * @param {Iterable<string>} held  directions maintenues
 * @param {number} heldSeconds     depuis le début de l'appui en cours
 * @param {number} dt              secondes écoulées depuis la dernière frame
 * @param {object} settings        panSpeed, panMaxSpeed, panAccelTime
 * @param {number} worldSize
 */
export function stepPan(camera, held, heldSeconds, dt, settings, worldSize) {
  let dx = 0;
  let dy = 0;
  for (const direction of held) {
    const dir = PAN_DIRECTIONS[direction];
    if (dir) { dx += dir.dx; dy += dir.dy; }
  }
  if ((dx === 0 && dy === 0) || !(dt > 0)) return { x: camera.x, y: camera.y };
  const norm = Math.hypot(dx, dy); // en diagonale, même vitesse qu'en ligne droite
  const distance = getPanSpeed(heldSeconds, settings) * Math.min(dt, 0.1);
  const limit = getPanLimit(worldSize);
  return {
    x: wrapX(camera.x + (dx / norm) * distance, worldSize),
    y: clamp(camera.y + (dy / norm) * distance, -limit, limit),
  };
}

/**
 * Translation libre de la caméra (trackpad, molette) : boucle en X, bornée au monde en Y.
 * @param {{x:number,y:number}} camera
 * @param {number} dx  unités monde
 * @param {number} dy
 */
export function translateCamera(camera, dx, dy, worldSize) {
  const limit = getPanLimit(worldSize);
  return {
    x: wrapX(camera.x + (Number.isFinite(dx) ? dx : 0), worldSize),
    y: clamp(camera.y + (Number.isFinite(dy) ? dy : 0), -limit, limit),
  };
}

/**
 * Coordonnées d'un point du monde dans la fenêtre du viseur (origine en haut à gauche). L'écart en
 * X passe par wrapX : avec `worldSize`, un point de l'autre côté de la couture (monde bouclé) se
 * projette par le chemin le plus court, pas par un détour de la largeur du monde. Sans `worldSize`,
 * comportement inchangé (simple différence, pas de bouclage).
 */
export function worldToView(point, camera, viewSize = VIEW_SIZE, worldSize, viewWidth = viewSize) {
  return {
    x: wrapX(point.x - camera.x, worldSize) + viewWidth / 2,
    y: point.y - camera.y + viewSize / 2,
  };
}

/**
 * Un point est-il visible (avec une marge) dans la fenêtre du viseur ? Sert au culling du rendu.
 * Avec `worldSize`, l'écart en X passe par wrapX (chemin le plus court à travers la couture). `viewWidth` : largeur de la vue
 * si elle est plus large que haute (paysage), `viewSize` en étant la hauteur.
 */
export function isInView(point, camera, margin = 0, viewSize = VIEW_SIZE, worldSize, viewWidth = viewSize) {
  const dx = wrapX(point.x - camera.x, worldSize);
  return Math.abs(dx) <= viewWidth / 2 + margin && Math.abs(point.y - camera.y) <= viewSize / 2 + margin;
}

/**
 * Élan après un glisser lancé (inertie) : la vitesse décroît exponentiellement (`friction` par
 * seconde) et s'annule sous `minSpeed`. Le déplacement renvoyé est l'intégrale exacte sur `dt`,
 * donc la glissade totale (≈ vitesse / friction) ne dépend pas de la cadence d'affichage.
 * @param {{x:number,y:number}} velocity  unités monde / s
 * @param {number} dt        secondes écoulées depuis la dernière frame
 * @param {number} friction  freinage (1/s) ; ≤ 0 = l'élan s'arrête net
 * @param {number} [minSpeed]  en dessous, l'élan est considéré comme éteint
 * @returns {{velocity:{x:number,y:number}, dx:number, dy:number}}
 */
export function stepInertia(velocity, dt, friction, minSpeed = 0) {
  if (!(friction > 0)) return { velocity: { x: 0, y: 0 }, dx: 0, dy: 0 };
  if (!(dt > 0)) return { velocity: { x: velocity.x, y: velocity.y }, dx: 0, dy: 0 }; // frame vide : l'élan attend
  const step = Math.min(dt, 0.1);
  const decay = Math.exp(-friction * step);
  const travel = (1 - decay) / friction;
  const next = { x: velocity.x * decay, y: velocity.y * decay };
  return {
    velocity: Math.hypot(next.x, next.y) < minSpeed ? { x: 0, y: 0 } : next,
    dx: velocity.x * travel,
    dy: velocity.y * travel,
  };
}
